import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Eye, EyeSlash, ArrowsClockwise, Copy, Check, X, Sparkle,
} from "@phosphor-icons/react";
import { generatePassword, passwordChecks, isPasswordValid, PASSWORD_RULES } from "@/lib/password";

/**
 * Input de contraseña con:
 * - Toggle mostrar/ocultar
 * - Generación automática (botón Sugerir)
 * - Copiar al portapapeles
 * - Checklist de cumplimiento en vivo
 *
 * Props:
 * - value, onChange, required (bool, default true), placeholder, testid
 */
export default function PasswordField({
  value = "",
  onChange,
  required = true,
  placeholder = "Contraseña",
  testid = "password-field",
}) {
  const [visible, setVisible] = useState(false);
  const checks = useMemo(() => passwordChecks(value), [value]);
  const valid = useMemo(() => isPasswordValid(value), [value]);
  const showChecklist = required || value.length > 0;

  const suggest = () => {
    const pw = generatePassword(12);
    onChange?.(pw);
    setVisible(true);
    toast.success("Contraseña sugerida generada");
  };

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Contraseña copiada al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div data-testid={testid}>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className={`w-full px-3 py-2.5 pr-28 border rounded-md outline-none transition-colors ${
            value && !valid && required
              ? "border-rose-300 focus:border-rose-500"
              : "border-neutral-200 focus:border-blue-500"
          }`}
          data-testid={`${testid}-input`}
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="p-1.5 text-neutral-400 hover:text-neutral-700 rounded-md"
            title={visible ? "Ocultar" : "Mostrar"}
            data-testid={`${testid}-toggle-visible`}
          >
            {visible ? <EyeSlash size={16} /> : <Eye size={16} />}
          </button>
          <button
            type="button"
            onClick={copy}
            disabled={!value}
            className="p-1.5 text-neutral-400 hover:text-neutral-700 rounded-md disabled:opacity-40"
            title="Copiar"
            data-testid={`${testid}-copy`}
          >
            <Copy size={16} />
          </button>
          <button
            type="button"
            onClick={suggest}
            className="px-2 py-1 ml-0.5 text-blue-700 hover:bg-blue-50 rounded-md inline-flex items-center gap-1 text-xs font-semibold"
            title="Generar contraseña sugerida"
            data-testid={`${testid}-suggest`}
          >
            <Sparkle size={14} weight="duotone" /> Sugerir
          </button>
        </div>
      </div>

      {showChecklist && (
        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-3" data-testid={`${testid}-checklist`}>
          {PASSWORD_RULES.map((r) => {
            const ok = !!checks[r.key];
            return (
              <li
                key={r.key}
                className={`flex items-center gap-1.5 text-[11px] ${ok ? "text-emerald-600" : "text-neutral-400"}`}
                data-testid={`${testid}-rule-${r.key}-${ok ? "ok" : "fail"}`}
              >
                {ok ? <Check size={12} weight="bold" /> : <X size={12} />}
                {r.label}
              </li>
            );
          })}
        </ul>
      )}

      {value && !valid && required && (
        <p className="mt-1 text-[11px] text-rose-600">La contraseña no cumple todas las reglas mínimas.</p>
      )}
    </div>
  );
}
